import { Button, ClickEvent, DataSource, DataSourceMode, DataSourceModeChangeEvent, Layout, SaveButton, SlideoutDecorator, TableColumn } from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { AgenciesWithPrimary } from "./AgenciesWithPrimary";
import { ResponsibilityUtil } from "./ResponsibilityUtil";
import { UserUtil } from "@mcleod/common";

/**
 * The purpose of this class is to display the provided layout in a SlideoutDecorator.  The provided layout is expected
 * to be a 'users info' layout from a master file.  For example, this would be the LocationUsers layout from the Location
 * master screen.  The important thing about the provided layout is that it is expected to contain an instance of
 * AgenciesWithPrimary as a nested layout.
 */
export class UsersResponsibilitySlideoutMF {
    private layout: Layout;
    private layoutName: string;
    private title: string;
    private agenciesLayoutHeaderTitle?: string;
    private customSlideoutWidth?: string;
    private idFilter: any;
    private primaryAgencyField: string;
    private sharedFieldNames?: string[];
    private isCommissionsEnabled: boolean;
    private enableCustomerCommissions: boolean;
    private agenciesWithPrimary: AgenciesWithPrimary;
    private layoutAgencyLinkDataSource: DataSource;
    private parentMainDataSource: DataSource;
    private parentAgencyLinkDataSource: DataSource;

    public static builder(): UsersResponsibilitySlideoutMFBuilder {
        return new UsersResponsibilitySlideoutMFBuilder();
    }

    public constructor(cfg: UsersResponsibilitySlideoutMFConfig) {
        this.layoutName = cfg.layoutName;
        this.title = cfg.title;
        this.agenciesLayoutHeaderTitle = cfg.agenciesLayoutHeaderTitle;
        this.customSlideoutWidth = cfg.customSlideoutWidth;
        this.idFilter = cfg.idFilter;
        this.primaryAgencyField = cfg.primaryAgencyField;
        this.parentMainDataSource = cfg.parentMainDataSource;
        this.parentAgencyLinkDataSource = cfg.parentAgencyLinkDataSource;
        this.isCommissionsEnabled = cfg.enableCommissions;
        this.enableCustomerCommissions = cfg.enableCustomerCommissions;
        this.layout = Layout.getLayout(cfg.layoutName);
        this.layout.addLayoutLoadListener(event => {
            this.layout.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
                this.agenciesWithPrimary?.setPrimaryTogglesFromParent();
            });
            this.agenciesWithPrimary = this.findAgenciesWithPrimaryNestedLayout(this.layout) as AgenciesWithPrimary;
            this.agenciesWithPrimary.commissionsEnabled = this.isCommissionsEnabled;
            if (this.agenciesWithPrimary != null) {
                this.layoutAgencyLinkDataSource = this.findAgencyLinkDataSource(this.layout);
                this.agenciesWithPrimary.displaySnackbars = true;
                this.agenciesWithPrimary.setup(this.layoutAgencyLinkDataSource, this.layout.mainDataSource, this.primaryAgencyField);
            }

            if (this.layoutName != "lme/ar/CustomerUsers" || (!this.isCommissionsEnabled || !this.enableCustomerCommissions)) {
                this.agenciesWithPrimary.agencyLinkTable.removeColumn("Commissions");
                this.agenciesWithPrimary.agencyLinkTable.removeColumn("Split %");
            }

            if (this.layoutName !== "lme/general/user/LMEUserAgencyUsers" || !this.isCommissionsEnabled) {
                this.agenciesWithPrimary.agencyLinkTable.removeColumn("columnHeaderPayCommWhen");
                this.agenciesWithPrimary.agencyLinkTable.removeColumn("columnHeaderEntryRole");
                this.agenciesWithPrimary.agencyLinkTable.removeColumn("columnHeaderDispatchRole");
                this.agenciesWithPrimary.agencyLinkTable.removeColumn("columnHeaderEffectiveDate");``
            }

            if (this.agenciesLayoutHeaderTitle) {
                this.agenciesWithPrimary.layoutheaderResponsibilityUsers.title = this.agenciesLayoutHeaderTitle;
            }
        });
    }

    public show() {
        const mode = this.parentMainDataSource.mode;
        const slideoutButton: Button | SaveButton = ResponsibilityUtil.getSlideoutButton(mode);
        if (slideoutButton != null && slideoutButton instanceof Button) {
            slideoutButton.addClickListener((event: ClickEvent) => {
                this.processSearchClick(event.target as Button, sod)
            });
        }
        const sod = new SlideoutDecorator({
            layout: this.layout,
            width: this.customSlideoutWidth ? this.customSlideoutWidth : "50%",
            maxWidth: UserUtil.SLIDEOUT_MAX_WIDTH,
            fillVerticalSpace: true,
            title: this.title,
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [slideoutButton],
            onClose: () => {
                sod.slideOut();
            },
            layoutLoadListeners: async () => {
                if ([DataSourceMode.UPDATE, DataSourceMode.ADD].includes(this.parentMainDataSource.mode)) {
                    slideoutButton.dataSource = sod.layout.mainDataSource;
                    if (this.idFilter != null) {
                        const response = await this.layout.mainDataSource.search({ id: this.idFilter });
                        this.layout.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, response.modelRows as ModelRow[]);
                    }
                }
            },
            doAfterSlideIn: (sod: SlideoutDecorator) => {
                if (this.parentMainDataSource.mode === DataSourceMode.UPDATE)
                    this.layout.mainDataSource.search({ ...this.idFilter });
                else if (this.parentMainDataSource.mode === DataSourceMode.ADD) {
                    if (this.layoutAgencyLinkDataSource != null) {
                        this.layoutAgencyLinkDataSource.data = this.parentAgencyLinkDataSource.getDataCopy();
                        this.layoutAgencyLinkDataSource.displayDataInBoundComponents();
                    }
                }
                this.layout.mainDataSource.mode = this.parentMainDataSource.mode;
                if (this.layoutAgencyLinkDataSource != null)
                    this.layoutAgencyLinkDataSource.mode = this.parentMainDataSource.mode;
            }
        });
    }

    private findAgenciesWithPrimaryNestedLayout(layout: Layout): AgenciesWithPrimary {
        for (const variable of Object.keys(layout)) {
            if (layout[variable] instanceof AgenciesWithPrimary) {
                return layout[variable];
            }
        }
        return null; //can return null if AgenciesWithPrimary nested layout was removed due to licensing
    }

    private findAgencyLinkDataSource(layout: Layout): DataSource {
        for (const dataSourceId of layout.dataSourceIDs) {
            if ((layout[dataSourceId] as DataSource).url === "lme/general/agency-link") {
                return layout[dataSourceId];
            }
        }
        throw new Error("The provided layout does not contain the expected Responsbility Codes DataSource.")
    }

    private async processSearchClick(button: Button, sod: SlideoutDecorator) {
        try {
            button.busy = true;
            if (this.parentMainDataSource.mode === DataSourceMode.SEARCH) {
                if (this.agenciesWithPrimary != null) {
                    if (this.sharedFieldNames && this.sharedFieldNames.length > 0) {
                        for (const field of this.sharedFieldNames) {
                            const value = this.layout.mainDataSource.activeRow.get(field);
                            if (value != null)
                                this.parentMainDataSource.searchRow.set(field, value);
                        }
                    } else if (this.primaryAgencyField != null) {
                        const primaryAgency = this.agenciesWithPrimary.getPrimaryAgencyId();
                        if (primaryAgency != null)
                            this.parentMainDataSource.searchRow.set(this.primaryAgencyField, primaryAgency);
                    }
                    this.cleanPrimaryField(this.layoutAgencyLinkDataSource.data);
                    this.parentAgencyLinkDataSource.data = this.layoutAgencyLinkDataSource.data;
                }
                sod.slideOut();
            }
        } finally {
            button.busy = false;
        }
    }

    private cleanPrimaryField(rows: ModelRow[]) {
        for (const row of rows) {
            delete row.data.is_primary;
        }
    }
}

interface UsersResponsibilitySlideoutMFConfig {
    layoutName: string;
    title: string;
    agenciesLayoutHeaderTitle?: string;
    customSlideoutWidth?: string;
    idFilter: any;
    primaryAgencyField: string;
    parentMainDataSource: DataSource;
    parentAgencyLinkDataSource: DataSource;
    enableCommissions: boolean;
    enableCustomerCommissions: boolean;
}

export class UsersResponsibilitySlideoutMFBuilder {
    private cfg: Partial<UsersResponsibilitySlideoutMFConfig> = {};

    layoutName(v: string) { this.cfg.layoutName = v; return this; }
    title(v: string) { this.cfg.title = v; return this; }
    agenciesLayoutHeaderTitle(v: string) { this.cfg.agenciesLayoutHeaderTitle = v; return this; }
    customeSlideoutWidth(v: string) { this.cfg.customSlideoutWidth = v; return this; }
    idFilter(v: any) { this.cfg.idFilter = v; return this; }
    primaryAgencyField(v: string) { this.cfg.primaryAgencyField = v; return this; }
    parentMainDataSource(v: DataSource) { this.cfg.parentMainDataSource = v; return this; }
    parentAgencyLinkDataSource(v: DataSource) { this.cfg.parentAgencyLinkDataSource = v; return this; }
    enableCommissions(flag: boolean): this { this.cfg.enableCommissions = flag; return this; }
    enableCustomerCommissions(flag: boolean): this { this.cfg.enableCustomerCommissions = flag; return this; }

    build(): UsersResponsibilitySlideoutMF {
        const missing: string[] = [];
        if (!this.cfg.layoutName) missing.push("layoutName");
        if (!this.cfg.title) missing.push("title");
        if (!this.cfg.parentMainDataSource) missing.push("parentMainDataSource");
        if (!this.cfg.parentAgencyLinkDataSource) missing.push("parentAgencyLinkDataSource");
        if (missing.length)
            throw new Error("UsersResponsibilitySlideoutMFBuilder missing required: " + missing.join(", "));
        return new UsersResponsibilitySlideoutMF(this.cfg as UsersResponsibilitySlideoutMFConfig);
    }
}
